/**
 * The range a criterion's values are entered on.
 *
 * A criterion pulled from a system attribute inherits that attribute's own
 * range: Impact and Effort are `select` attributes whose options are
 * Low / Medium / High, and asking someone to type "7" for Impact when their
 * workspace defines Impact as three named bands is asking a question the
 * workspace never posed. The phone offered nothing but a 0–10 box.
 *
 * Values are still stored as numbers on 0–VALUE_MAX — that is what
 * accepted_values holds, what the ranking computes on, and what every other
 * surface reads. A banded range is a presentation of that scale, not a second
 * store: each band owns an equal slice of 0–10 and is written as the midpoint
 * of its slice. Three bands give Low 1.7, Medium 5, High 8.3; a value that
 * arrives from anywhere else displays as whichever band's slice contains it.
 */
import { VALUE_MAX, VALUE_MIN } from './trackBoundSessionSeed';
import type { ValueRange } from '../stores/prioritization/attributeGovernanceStore';

export interface NumericRange {
  kind: 'numeric';
  min: number;
  max: number;
}

export interface BandedRange {
  kind: 'banded';
  /** Band labels, lowest first. */
  bands: string[];
}

export type CriterionRange = NumericRange | BandedRange;

export const NUMERIC_RANGE: NumericRange = { kind: 'numeric', min: VALUE_MIN, max: VALUE_MAX };

/** The range an attribute implies, or the numeric scale when it implies none. */
export function rangeForAttribute(
  attribute: { type?: string | null; options?: unknown } | null | undefined,
): CriterionRange {
  const options = Array.isArray(attribute?.options)
    ? (attribute?.options as unknown[]).map((o) => String(o)).filter((o) => o.trim().length > 0)
    : [];
  if (attribute?.type === 'select' && options.length >= 2) {
    return { kind: 'banded', bands: options };
  }
  return NUMERIC_RANGE;
}

/** The stored number a band means: the midpoint of its equal slice of 0–VALUE_MAX. */
export function valueForBand(range: BandedRange, bandIndex: number): number {
  const n = range.bands.length;
  if (n === 0) return 0;
  const width = (VALUE_MAX - VALUE_MIN) / n;
  const midpoint = VALUE_MIN + width * (bandIndex + 0.5);
  return Math.round(midpoint * 10) / 10;
}

/** The band a stored number falls in, or null when there is no value yet. */
export function bandForValue(range: BandedRange, value: number | undefined): number | null {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  const n = range.bands.length;
  if (n === 0) return null;
  const width = (VALUE_MAX - VALUE_MIN) / n;
  const raw = Math.floor((value - VALUE_MIN) / width);
  return Math.min(n - 1, Math.max(0, raw));
}

/** How a stored value reads on this range — a band label, or the number itself. */
export function describeValue(range: CriterionRange, value: number | undefined): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  if (range.kind === 'numeric') return String(value);
  const idx = bandForValue(range, value);
  return idx === null ? '' : range.bands[idx];
}

/** A track criterion's range in a few words, as the track's Criteria & Scales tab and the cycle configure wizard show it. */
export function formatRangeDisplay(range: ValueRange | undefined): string {
  if (!range) return '—';
  switch (range.type) {
    case 'number':
      return [range.min, range.max].filter(n => n != null).length
        ? `${range.min ?? '?'}–${range.max ?? '?'}${range.step != null ? ` step ${range.step}` : ''}`
        : 'Number (not set)';
    case 'select':
    case 'scale':
      return range.options?.length
        ? range.options.join(', ')
        : `${range.type} (no options)`;
    case 'fibonacci':
      return range.options?.length ? `Fibonacci: ${range.options.join(', ')}` : 'Fibonacci';
    case 'custom':
      return range.options?.length ? `Custom: ${range.options.join(', ')}` : 'Custom';
    default:
      return range.type || '—';
  }
}
